import { getTimeTillEndOfDayMs } from '@helpers';
import { queryOptions, useQueries } from '@tanstack/react-query';

type EarningsInsightQueryFn<TData> = (
  earningsInsightId: string,
  signal?: AbortSignal
) => Promise<TData>;

interface CreateEarningsInsightQueryOptionsProps<TData> {
  earningsInsightId: string;
  queryKey: string;
  queryFn: EarningsInsightQueryFn<TData>;
  enabled?: boolean;
}

export const createEarningsInsightQueryOptions = <TData>({
  earningsInsightId,
  queryKey,
  queryFn,
  enabled,
}: CreateEarningsInsightQueryOptionsProps<TData>) =>
  queryOptions({
    queryKey: [queryKey, earningsInsightId],
    queryFn: ({ signal }) => queryFn(earningsInsightId, signal),
    staleTime: getTimeTillEndOfDayMs(),
    enabled,
  });

interface UseEarningsInsightQueriesProps<TData> {
  earningsInsightIds: string[];
  queryKey: string;
  queryFn: EarningsInsightQueryFn<TData>;
}

export const useEarningsInsightQueries = <TData>({
  earningsInsightIds,
  queryKey,
  queryFn,
}: UseEarningsInsightQueriesProps<TData>) =>
  useQueries({
    queries: earningsInsightIds.map(earningsInsightId =>
      createEarningsInsightQueryOptions({
        earningsInsightId,
        queryKey,
        queryFn,
      })
    ),
    combine: results => ({
      data: results
        .map(result => result.data)
        .filter((data): data is TData => data != null),
      isFetching: results.some(result => result.isFetching),
    }),
  });
